import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  Control,
  Controller,
  FieldErrors,
  useFormContext,
  UseFormWatch,
} from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Trash2, AlertCircle, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { DayConfig, FacilitySchedule } from "@/types/facility/schedule";
import { format, addDays, isWithinInterval } from "date-fns";
import { useGetFacilityByIdQuery } from "@/services/facilityApis";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import SlotsandSchedule from "./slots-and-schedule";

type DaysState = {
  [key: string]: DayConfig;
};

interface EventAvailabilityFormProps {
  control: Control<FacilitySchedule>;
  errors: FieldErrors<FacilitySchedule>;
  watch: UseFormWatch<FacilitySchedule>;
  open: boolean;
}

const EventAvailability = ({
  control,
  errors,
  watch,
  open,
}: EventAvailabilityFormProps) => {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { setValue, getValues } = useFormContext();

  // Watch for start_date, end_date, and always_available changes
  const startDate = watch("general_info.start_date") as string | undefined;
  const endDate = watch("general_info.end_date") as string | undefined;
  const alwaysAvailable = watch("general_info.always_available");
  const facilityId = watch("general_info.facility_id");
  const durationPerSlot = watch("general_info.duration_per_slot");
  const formAvailability = watch("general_info.availability");

  // Fetch facility data when facility ID changes
  const { data: facilityData, isLoading: isFacilityLoading } =
    useGetFacilityByIdQuery({ id: facilityId, org_id: orgId }, { skip: !facilityId });

  const dayNames = {
    Monday: "Monday",
    Tuesday: "Tuesday",
    Wednesday: "Wednesday",
    Thursday: "Thursday",
    Friday: "Friday",
    Saturday: "Saturday",
    Sunday: "Sunday",
  };

  const initialDaysState: DaysState = {
    Monday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
    Tuesday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
    Wednesday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
    Thursday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
    Friday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
    Saturday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
    Sunday: {
      is_available: false,
      time_slots: { start_time: "", end_time: "" },
    },
  };

  const [dayErrors, setDayErrors] = useState<{
    [key: string]: { start_time?: string; end_time?: string };
  }>({});

  // Helper to convert API availability (lowercase) to component days state (capitalized)
  const convertAvailabilityToDays = (availability: any): DaysState => {
    if (!availability) return initialDaysState;
    
    return {
      Monday: availability.monday || initialDaysState.Monday,
      Tuesday: availability.tuesday || initialDaysState.Tuesday,
      Wednesday: availability.wednesday || initialDaysState.Wednesday,
      Thursday: availability.thursday || initialDaysState.Thursday,
      Friday: availability.friday || initialDaysState.Friday,
      Saturday: availability.saturday || initialDaysState.Saturday,
      Sunday: availability.sunday || initialDaysState.Sunday,
    };
  };

  // Get existing days data from form or use initial state
  const existingDays = getValues("general_info.availability");
  const [days, setDays] = useState<DaysState>(convertAvailabilityToDays(existingDays));

  // Store available days from facility
  const [facilityAvailableDays, setFacilityAvailableDays] = useState<string[]>(
    []
  );
  const [facilityOperatingHours, setFacilityOperatingHours] = useState<{
    [key: string]: { start_time: string; end_time: string };
  }>({});
  const [facilityOriginalSlotDuration, setFacilityOriginalSlotDuration] =
    useState<number>(0);

  // State for toggling form visibility
  const [isFormVisible, setIsFormVisible] = useState<boolean>(true);

  // Helper function to round time to nearest 5 minutes (defined early for use in useEffects)
  const roundToNearest5Minutes = (timeString: string): string => {
    if (!timeString) return timeString;

    const [hours, minutes] = timeString.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes;
    const roundedMinutes = Math.round(totalMinutes / 5) * 5;

    const roundedHours = Math.floor(roundedMinutes / 60);
    const finalMinutes = roundedMinutes % 60;

    return `${roundedHours.toString().padStart(2, '0')}:${finalMinutes.toString().padStart(2, '0')}`;
  };

  // Sync days state with form availability values (for edit mode)
  useEffect(() => {
    if (formAvailability) {
      const convertedDays = convertAvailabilityToDays(formAvailability);
      // Only update if there are actual differences to avoid unnecessary re-renders
      setDays((prev) => {
        const hasChanges = Object.keys(convertedDays).some(
          (day) =>
            prev[day].is_available !== convertedDays[day].is_available ||
            prev[day].time_slots.start_time !== convertedDays[day].time_slots.start_time ||
            prev[day].time_slots.end_time !== convertedDays[day].time_slots.end_time
        );
        return hasChanges ? convertedDays : prev;
      });
    }
  }, [formAvailability]);

  // Update facility available days when facility data changes
  useEffect(() => {
    if (facilityData) {
      const facility = facilityData;

      // Extract available days and operating hours from facility data
      const availableDays: string[] = [];
      const operatingHours: {
        [key: string]: { start_time: string; end_time: string };
      } = {};

      if (facility.availability) {
        // Process each day in the facility's availability
        Object.entries(facility.availability).forEach(([day, dayConfig]) => {
          if (dayConfig.is_available) {
            // Format day name with first letter capitalized (monday -> Monday)
            const dayName = day.charAt(0).toUpperCase() + day.slice(1);
            availableDays.push(dayName);

            // Only set operating hours if time_slots exist and have valid times
            if (dayConfig.time_slots?.start_time && dayConfig.time_slots?.end_time) {
              operatingHours[dayName] = {
                start_time: dayConfig.time_slots.start_time,
                end_time: dayConfig.time_slots.end_time,
              };
            }
          }
        });
      }

      setFacilityAvailableDays(availableDays);
      setFacilityOperatingHours(operatingHours);

      // Only update days that don't have times set (don't overwrite existing availability)
      // Check if form already has availability data (edit mode) - if so, don't modify
      const existingFormAvailability = getValues("general_info.availability");
      const hasExistingAvailability = existingFormAvailability && 
        Object.values(existingFormAvailability).some(
          (day: any) => day?.is_available && day?.time_slots?.start_time && day?.time_slots?.end_time
        );

      // Only auto-fill facility hours if we don't have existing availability (new form)
      if (!hasExistingAvailability) {
        setDays((prev) => {
          const newDays = { ...prev };
          let hasChanges = false;

          Object.keys(newDays).forEach((day) => {
            // Only fill in times if day is available but has no times set
            if (newDays[day].is_available) {
              const facilityHours = operatingHours[day];
              const currentStartTime = newDays[day].time_slots.start_time;
              const currentEndTime = newDays[day].time_slots.end_time;

              // Only set facility hours if times are completely empty (preserve existing data)
              if (facilityHours && !currentStartTime && !currentEndTime) {
                newDays[day].time_slots = {
                  start_time: roundToNearest5Minutes(facilityHours.start_time),
                  end_time: roundToNearest5Minutes(facilityHours.end_time),
                };
                hasChanges = true;
              }
            }
          });

          return hasChanges ? newDays : prev;
        });
      }
    }
  }, [facilityData, getValues, setValue]);

  // Function to get day name from date
  const getDayName = (date: Date): keyof typeof dayNames => {
    const dayIndex = date.getDay();
    const daysOfWeek = [
      "Sunday",    // 0
      "Monday",    // 1
      "Tuesday",   // 2
      "Wednesday", // 3
      "Thursday",  // 4
      "Friday",    // 5
      "Saturday",  // 6
    ];
    return daysOfWeek[dayIndex] as keyof typeof dayNames;
  };

  // Determine which days to display based on start and end dates
  // AND facility available days
  const getVisibleDays = (): string[] => {
    // First get days based on date range
    let dateRangeDays: string[] = [];

    // If we don't have a start date or always_available is true, include all days
    if (!startDate || alwaysAvailable) {
      dateRangeDays = Object.keys(dayNames);
    } else {
      // Convert string dates to Date objects
      const startDateObj = new Date(startDate);
      const endDateObj = endDate ? new Date(endDate) : null;

      // If we have a start date but no end date, just show the day of the start date
      if (!endDate) {
        dateRangeDays = [getDayName(startDateObj)];
      }
      // If start and end dates are the same day, only show that day
      else if (startDate === endDate) {
        dateRangeDays = [getDayName(startDateObj)];
      }
      // If we have both start and end dates, find all the days that fall in the range
      else if (endDateObj) {
        const daysInRange = new Set<string>();

        // Add the start date's day
        daysInRange.add(getDayName(startDateObj));

        // Calculate all days between start and end (inclusive)
        let currentDate = startDateObj;
        while (
          format(currentDate, "yyyy-MM-dd") !== format(endDateObj, "yyyy-MM-dd")
        ) {
          currentDate = addDays(currentDate, 1);
          daysInRange.add(getDayName(currentDate));
        }

        dateRangeDays = Array.from(daysInRange);
      }
    }

    // Now filter by facility available days if we have a facility selected
    // Always filter when facility is selected, even if no days are available
    if (facilityId) {
      return dateRangeDays.filter((day) => facilityAvailableDays.includes(day));
    }

    return dateRangeDays;
  };

  const visibleDays = getVisibleDays();

  // Calculate time slots for a given day
  const calculateTimeSlots = (day: string): string[] => {
    const dayConfig = days[day];
    if (
      !dayConfig.is_available ||
      !dayConfig.time_slots.start_time ||
      !dayConfig.time_slots.end_time ||
      !durationPerSlot
    ) {
      return [];
    }

    const slots: string[] = [];
    const startTime = dayConfig.time_slots.start_time;
    const endTime = dayConfig.time_slots.end_time;

    // Check if this is an overnight range
    const isOvernight = isOvernightRange(startTime, endTime);

    // Convert times to minutes
    let currentMinutes = timeToMinutes(startTime);
    let totalEndMinutes = timeToMinutes(endTime);

    // If overnight, adjust end time to be next day (add 24 hours)
    if (isOvernight) {
      totalEndMinutes += 24 * 60; // Add 24 hours for next day
    }

    const slotDurationMinutes = durationPerSlot * 60;

    // FOR EVENTS: Create only ONE slot per day
    // Find the first whole hour within the available time range
    // that can fit the event duration

    // Round up start time to the next whole hour
    let slotStartMinutes = currentMinutes;
    const minutesIntoHour = slotStartMinutes % 60;

    // If not at a whole hour, round up to next whole hour
    if (minutesIntoHour !== 0) {
      slotStartMinutes = slotStartMinutes + (60 - minutesIntoHour);
    }

    // Check if the slot can fit within the available hours
    if (slotStartMinutes + slotDurationMinutes <= totalEndMinutes) {
      // Calculate slot start time (modulo 24 hours to handle overnight)
      const slotStartMinutesOfDay = slotStartMinutes % (24 * 60);
      const slotStartHours = Math.floor(slotStartMinutesOfDay / 60);
      const slotStartMinutesRemainder = slotStartMinutesOfDay % 60;
      const slotStart = `${slotStartHours.toString().padStart(2, "0")}:${slotStartMinutesRemainder.toString().padStart(2, "0")}`;

      // Calculate slot end time (modulo 24 hours to handle overnight)
      const slotEndTotalMinutes = slotStartMinutes + slotDurationMinutes;
      const slotEndMinutesOfDay = slotEndTotalMinutes % (24 * 60);
      const slotEndHours = Math.floor(slotEndMinutesOfDay / 60);
      const slotEndMinutesRemainder = slotEndMinutesOfDay % 60;
      const slotEnd = `${slotEndHours.toString().padStart(2, "0")}:${slotEndMinutesRemainder.toString().padStart(2, "0")}`;

      slots.push(`${slotStart} - ${slotEnd}`);
    }

    return slots;
  };

  // Listen for external validation errors
  useEffect(() => {
    const handleDayErrors = (event: any) => {
      const { day, errors } = event.detail;
      if (day && errors) {
        setDayErrors((prev) => ({
          ...prev,
          [day]: errors,
        }));
      }
    };

    document.addEventListener("update-day-errors", handleDayErrors as any);

    return () => {
      document.removeEventListener("update-day-errors", handleDayErrors as any);
    };
  }, []);

  // Expose dayErrors to parent form via custom event
  useEffect(() => {
    const event = new CustomEvent("availability-time-errors", {
      detail: { errors: dayErrors, hasErrors: Object.keys(dayErrors).length > 0 && Object.values(dayErrors).some(err => err.start_time || err.end_time) },
    });
    document.dispatchEvent(event);
  }, [dayErrors]);

  useEffect(() => {
    // Update form values whenever days change
    setValue(
      "general_info.availability",
      {
        monday: days.Monday,
        tuesday: days.Tuesday,
        wednesday: days.Wednesday,
        thursday: days.Thursday,
        friday: days.Friday,
        saturday: days.Saturday,
        sunday: days.Sunday,
      },
      { shouldDirty: true, shouldTouch: true }
    );
  }, [days, setValue]);

  // Re-validate all times when durationPerSlot changes
  useEffect(() => {
    if (durationPerSlot && Object.keys(facilityOperatingHours).length > 0 && Object.keys(days).length > 0) {
      // Re-validate all available days when duration changes
      // Use a small delay to ensure this runs after any state updates
      const timeoutId = setTimeout(() => {
        Object.keys(days).forEach((day) => {
          const dayConfig = days[day];
          if (dayConfig.is_available && dayConfig.time_slots.start_time && dayConfig.time_slots.end_time) {
            // Trigger re-validation by calling updateTime for both fields
            // This will re-run all validation logic including duration checks
            updateTime(day, "start_time", dayConfig.time_slots.start_time);
            updateTime(day, "end_time", dayConfig.time_slots.end_time);
          }
        });
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durationPerSlot]);

  // Reset days that are no longer in the date range or facility available days
  useEffect(() => {
    if ((startDate && endDate && !alwaysAvailable) || facilityId) {
      // Get days that should be visible based on date range and facility
      const validDays = getVisibleDays();

      // Reset any days that aren't in the valid range
      setDays((prev) => {
        const newDays = { ...prev };

        Object.keys(newDays).forEach((day) => {
          if (!validDays.includes(day) && newDays[day].is_available) {
            newDays[day] = {
              is_available: false,
              time_slots: { start_time: "", end_time: "" },
            };
          }
        });

        return newDays;
      });
    }
  }, [startDate, endDate, alwaysAvailable, facilityAvailableDays]);



  const toggleDay = (day: string) => {
    const wasAvailable = days[day].is_available;
    
    // If toggling OFF (unselecting), clear times
    if (wasAvailable) {
      clearDayTimes(day);
      return;
    }

    // If toggling ON (selecting), set default times
    setDays((prev) => {
      const newState = {
        ...prev,
        [day]: {
          ...prev[day],
          is_available: !prev[day].is_available,
        },
      };

      const facilityHours = facilityOperatingHours[day];

      if (facilityHours && facilityHours.start_time && facilityHours.end_time) {
        // Round facility start time to nearest 5 minutes
        const roundedStartTime = roundToNearest5Minutes(facilityHours.start_time);
        
        // If duration_per_slot is set, calculate end_time = start_time + duration_per_slot
        // Otherwise, use facility end time
        let endTime: string;
        
        if (durationPerSlot && durationPerSlot > 0) {
          // Calculate end time based on start time + duration
          const startMinutes = timeToMinutes(roundedStartTime);
          const durationMinutes = Math.round(durationPerSlot * 60);
          const endMinutes = startMinutes + durationMinutes;
          
          const hours = Math.floor(endMinutes / 60);
          const minutes = endMinutes % 60;
          // Handle overnight (next day)
          const displayHours = hours % 24;
          
          endTime = `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
          
          // Validate that end time is within facility hours
          const facilityIsOvernight = isOvernightRange(facilityHours.start_time, facilityHours.end_time);
          if (!isTimeWithinFacilityHours(endTime, facilityHours.start_time, facilityHours.end_time)) {
            // If calculated end time exceeds facility hours, use facility end time instead
            endTime = roundToNearest5Minutes(facilityHours.end_time);
          }
        } else {
          // No duration set, use facility end time
          endTime = roundToNearest5Minutes(facilityHours.end_time);
        }
        
        newState[day].time_slots = {
          start_time: roundedStartTime,
          end_time: endTime,
        };
      } else {
        // No facility hours available, keep empty and don't allow toggling
        // This should not happen if getVisibleDays is working correctly
        newState[day].is_available = false;
        newState[day].time_slots = {
          start_time: "",
          end_time: "",
        };
      }

      return newState;
    });

    // Clear errors when toggling day
    setDayErrors((prev) => ({
      ...prev,
      [day]: {},
    }));
  };

  const updateTime = (
    day: string,
    field: "start_time" | "end_time",
    value: string
  ) => {
    // Use the raw typed value to allow free-form entry; validation will handle errors
    const typedValue = value;

    // Get current facility hours for this day
    const facilityHours = facilityOperatingHours[day];

    if (!facilityHours) {
      // If no facility hours, don't allow any changes
      setDayErrors((prev) => ({
        ...prev,
        [day]: {
          ...prev[day],
          [field]: "Facility operating hours not available",
        },
      }));
      return;
    }

    // First update the state with the new value
    setDays((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        time_slots: {
          ...prev[day].time_slots,
          [field]: typedValue,
        },
      },
    }));

    // Then validate the updated state
    const updatedDays = {
      ...days,
      [day]: {
        ...days[day],
        time_slots: {
          ...days[day].time_slots,
          [field]: typedValue,
        },
      },
    };

    const updatedDayConfig = updatedDays[day];
    const startTime = updatedDayConfig.time_slots.start_time;
    const endTime = updatedDayConfig.time_slots.end_time;

    // Initialize errors for the current day
    let newDayErrors: { start_time?: string; end_time?: string } = { ...dayErrors[day] };

    // Clear previous errors for the specific field that was updated first
    if (newDayErrors[field]) {
      delete newDayErrors[field];
    }

    // Also clear the other field's error if it was a duration conflict originating from the current field
    if (field === "start_time" && newDayErrors.end_time && newDayErrors.end_time.includes('less than event duration')) {
      delete newDayErrors.end_time;
    }
    if (field === "end_time" && newDayErrors.start_time && newDayErrors.start_time.includes('less than event duration')) {
      delete newDayErrors.start_time;
    }

    // Check if facility hours are overnight
    const facilityIsOvernight = isOvernightRange(facilityHours.start_time, facilityHours.end_time);

    // Validate start time
    if (startTime) {
      const startBoundaryCheck = checkTimeBoundary(startTime, facilityHours.start_time, facilityHours.end_time);

      if (startBoundaryCheck === "before" || startBoundaryCheck === "after") {
        newDayErrors = {
          ...newDayErrors,
          start_time: "Event time cannot be outside the facility operating hours.",
        };
      }

      // Validate start time vs end time (allow overnight ranges for event slots if they are valid)
      if (endTime) {
        const isEventOvernight = isOvernightRange(startTime, endTime);
        const startMinutes = timeToMinutes(startTime);
        const endMinutes = timeToMinutes(endTime);

        // If it's NOT an overnight range, then start time must be strictly before end time.
        // If it IS an overnight range, then start >= end is expected and valid.
        if (!isEventOvernight && startMinutes >= endMinutes) {
          newDayErrors = {
            ...newDayErrors,
            start_time: "Start time must be before end time for a single-day event.",
          };
        }
      }
    }

    // Validate end time
    if (endTime) {
      const endBoundaryCheck = checkTimeBoundary(endTime, facilityHours.start_time, facilityHours.end_time);

      if (endBoundaryCheck === "before" || endBoundaryCheck === "after") {
        newDayErrors = {
          ...newDayErrors,
          end_time: "Event time cannot be outside the facility operating hours.",
        };
      }

      // Validate end time vs start time (allow overnight ranges for event slots if they are valid)
      if (startTime) {
        const isEventOvernight = isOvernightRange(startTime, endTime);
        const startMinutes = timeToMinutes(startTime);
        const endMinutes = timeToMinutes(endTime);

        // If it's NOT an overnight range, then end time must be strictly after start time.
        // If it IS an overnight range, then end <= start is expected and valid.
        if (!isEventOvernight && startMinutes >= endMinutes) {
          newDayErrors = {
            ...newDayErrors,
            end_time: "End time must be after start time for a single-day event.",
          };
        }
      }
    }

    // Validate time range against duration per slot
    if (startTime && endTime && durationPerSlot) {
      const timeRangeMinutes = getDurationMinutes(startTime, endTime);
      const durationPerSlotMinutes = durationPerSlot * 60;

      // Check if event duration is less than total available time in the facility slot
      if (timeRangeMinutes < durationPerSlotMinutes) {
        // This means the event cannot fit within the selected facility time range
        const errorMessage = `Available time (${(timeRangeMinutes / 60).toFixed(1)} hours) is less than event duration (${durationPerSlot} hour${durationPerSlot !== 1 ? 's' : ''})`;
        newDayErrors = {
          ...newDayErrors,
          [field]: errorMessage,
          // Show error on the field that was changed, and also on end_time for clarity
          ...(field === 'start_time' ? { end_time: newDayErrors.end_time || errorMessage } : {}),
        };
      } else if (timeRangeMinutes > durationPerSlotMinutes) {
        // If the facility time range is greater than the event duration, it's fine.
        // We just need to ensure that the event slot calculation correctly fits.
        // Clear any previous duration-related errors if the new input is valid.
        if (newDayErrors[field] && newDayErrors[field].includes('Available time (') && newDayErrors[field].includes('is less than event duration')) {
          delete newDayErrors[field];
        }
        // Also clear from end_time if it was set there and we're updating start_time
        if (field === 'start_time' && newDayErrors.end_time && newDayErrors.end_time.includes('Available time (') && newDayErrors.end_time.includes('is less than event duration')) {
          delete newDayErrors.end_time;
        }

      } else {
        // Exact match, no error
        if (newDayErrors[field] && newDayErrors[field].includes('Available time (') && newDayErrors[field].includes('is less than event duration')) {
          delete newDayErrors[field];
        }
        // Also clear from end_time if it was set there and we're updating start_time
        if (field === 'start_time' && newDayErrors.end_time && newDayErrors.end_time.includes('Available time (') && newDayErrors.end_time.includes('is less than event duration')) {
          delete newDayErrors.end_time;
        }
      }
    }

    // Finally, update the dayErrors state once
    setDayErrors((prev) => {
      const updatedErrors = { ...prev };
      if (Object.keys(newDayErrors).length > 0) {
        updatedErrors[day] = newDayErrors;
      } else {
        delete updatedErrors[day];
      }
      return updatedErrors;
    });
  };

  const clearDayTimes = (day: string) => {
    setDays((prev) => ({
      ...prev,
      [day]: {
        is_available: false,
        time_slots: { start_time: "", end_time: "" },
      },
    }));

    // Clear errors when clearing times
    setDayErrors((prev) => ({
      ...prev,
      [day]: {},
    }));
  };

  // Helper function to convert time string to minutes (0-1439 for same day, or 1440+ for next day)
  const timeToMinutes = (timeString: string): number => {
    if (!timeString) return 0;
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper function to check if time range spans midnight (overnight)
  const isOvernightRange = (startTime: string, endTime: string): boolean => {
    if (!startTime || !endTime) return false;
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    return endMinutes <= startMinutes; // If end <= start, it's overnight
  };

  // Helper function to get total duration in minutes for a time range (handles overnight)
  const getDurationMinutes = (startTime: string, endTime: string): number => {
    if (!startTime || !endTime) return 0;
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);

    if (isOvernightRange(startTime, endTime)) {
      // Overnight: end time is next day, so add 24 hours
      return (24 * 60) - startMinutes + endMinutes;
    }
    return endMinutes - startMinutes;
  };

  // Helper function to check if time is within facility hours (handles overnight facility hours)
  const isTimeWithinFacilityHours = (
    time: string,
    facilityStart: string,
    facilityEnd: string
  ): boolean => {
    if (!time || !facilityStart || !facilityEnd) return false;

    const timeMinutes = timeToMinutes(time);
    const facilityStartMinutes = timeToMinutes(facilityStart);
    const facilityEndMinutes = timeToMinutes(facilityEnd);

    // Check if facility hours are overnight
    const facilityIsOvernight = facilityEndMinutes <= facilityStartMinutes;

    if (facilityIsOvernight) {
      // Facility hours span midnight (e.g., 20:00 to 02:00)
      // Time is valid if it's >= facilityStart OR <= facilityEnd
      return timeMinutes >= facilityStartMinutes || timeMinutes <= facilityEndMinutes;
    } else {
      // Normal facility hours (e.g., 08:00 to 20:00)
      return timeMinutes >= facilityStartMinutes && timeMinutes <= facilityEndMinutes;
    }
  };

  // Helper function to check if a time is before or after facility hours
  const checkTimeBoundary = (
    time: string,
    facilityStart: string,
    facilityEnd: string
  ): "before" | "after" | "within" | "outside" => {
    if (!time || !facilityStart || !facilityEnd) return "within";

    const timeMinutes = timeToMinutes(time);
    const facilityStartMinutes = timeToMinutes(facilityStart);
    const facilityEndMinutes = timeToMinutes(facilityEnd);

    const facilityIsOvernight = facilityEndMinutes <= facilityStartMinutes;

    if (facilityIsOvernight) {
      if (timeMinutes < facilityStartMinutes && timeMinutes > facilityEndMinutes) {
        return "outside"; // Neither before start nor after end in overnight scenario
      }
      if (timeMinutes < facilityStartMinutes) return "before";
      if (timeMinutes > facilityEndMinutes) return "after";
      return "within";
    } else {
      if (timeMinutes < facilityStartMinutes) return "before";
      if (timeMinutes > facilityEndMinutes) return "after";
      return "within";
    }
  };

  // Helper function to format time for display
  const formatTimeRange = (startTime: string, endTime: string): string => {
    return `${startTime} - ${endTime}`;
  };

  // Check if at least one day is selected
  const hasSelectedDays = Object.values(days).some(day => day.is_available);

  // Check if any selected day has invalid slot configuration (duration too large)
  const hasInvalidSlotConfiguration = Object.entries(days).some(([day, dayConfig]) => {
    if (!dayConfig.is_available || !dayConfig.time_slots.start_time || !dayConfig.time_slots.end_time || !durationPerSlot) {
      return false;
    }

    const timeSlots = calculateTimeSlots(day);
    return timeSlots.length === 0; // No slots can fit = invalid configuration
  });

  const dayRowGrid =
    "grid grid-cols-[90px_minmax(0,1fr)_minmax(0,1fr)_40px] sm:grid-cols-[130px_minmax(0,1fr)_minmax(0,1fr)_64px] gap-2 sm:gap-3 items-start w-full mb-2";

  return (
    <div className="space-y-4" data-component="event-availability">
      <div className="flex flex-col gap-4 xl:flex-row relative">
        {/* Toggle button to show form when hidden - positioned on left edge of calendar */}
        {!isFormVisible && (
          <button
            onClick={() => setIsFormVisible(true)}
            className="absolute top-0 -left-1.5 z-30 size-6 flex items-center justify-center rounded-md bg-white transition border-[1px] border-gray-200 shadow-sm hover:shadow-md hover:bg-gray-50"
            aria-label="Show availability form"
            title="Show availability form"
          >
            <ChevronRight className="text-gray-700 font-bold" size={14} />
          </button>
        )}

        {isFormVisible && (
          <Card className={cn(
            "w-full xl:w-[42%] transition-all duration-300 ease-in-out"
          )}>
            <CardHeader className="relative px-4 sm:px-6">
              <CardTitle className="text-base font-medium pr-8">
                Availability
                {!alwaysAvailable && startDate && endDate && (
                  <span className="ml-2 text-xs sm:text-sm text-gray-500 font-normal">
                    ({format(new Date(startDate), "dd MMM yyyy")} to{" "}
                    {format(new Date(endDate), "dd MMM yyyy")})
                  </span>
                )}
              </CardTitle>
              {/* Toggle button to hide form - positioned in header */}
              <button
                onClick={() => setIsFormVisible(false)}
                className="absolute top-4 right-4 z-10 size-6 flex items-center justify-center rounded-md bg-white transition border-[1px] border-gray-200 shadow-sm hover:shadow-md hover:bg-gray-50"
                aria-label="Hide availability form"
                title="Hide availability form to view calendar in full"
              >
                <ChevronLeft className="text-gray-700 font-bold" size={14} />
              </button>
              <Separator className="h-[1px]" />
            </CardHeader>
            <CardContent className="space-y-6 px-3 sm:px-6">
              <div className="flex gap-4">
                <div className="w-full sm:w-1/2">
                  <div className="space-y-2">
                    <Label className="text-base">
                      Duration Per Slot (Hours) <span className="text-red-500">*</span>
                    </Label>
                    <Controller
                      control={control}
                      name="general_info.duration_per_slot"
                      rules={{
                        required: "Required",
                        min: {
                          value: 1,
                          message: "Required",
                        },
                        validate: {
                          slotsCanFit: (value) => {
                            if (!value) return true; // Let required validation handle empty values

                            // Get current form values for availability to ensure we're validating against latest data
                            const currentAvailability = getValues("general_info.availability");
                            if (!currentAvailability) return true;

                            // Convert form availability to days format for validation
                            const currentDays = {
                              Monday: currentAvailability.monday,
                              Tuesday: currentAvailability.tuesday,
                              Wednesday: currentAvailability.wednesday,
                              Thursday: currentAvailability.thursday,
                              Friday: currentAvailability.friday,
                              Saturday: currentAvailability.saturday,
                              Sunday: currentAvailability.sunday,
                            };

                            // Check if any selected day has valid slots
                            const selectedDays = Object.entries(currentDays).filter(([_, dayConfig]) =>
                              dayConfig?.is_available &&
                              dayConfig?.time_slots?.start_time &&
                              dayConfig?.time_slots?.end_time
                            );

                            if (selectedDays.length === 0) return true; // No days selected yet

                            // Check if at least one day can accommodate the duration
                            const hasValidSlots = selectedDays.some(([day, dayConfig]) => {
                              const startTime = dayConfig.time_slots.start_time;
                              const endTime = dayConfig.time_slots.end_time;

                              // Calculate available time in hours (handles overnight ranges)
                              const availableMinutes = getDurationMinutes(startTime, endTime);
                              const availableHours = availableMinutes / 60;

                              return availableHours >= value;
                            });

                            return hasValidSlots;
                          }
                        }
                      }}
                      render={({
                        field: { onChange, value },
                        fieldState: { error },
                      }) => (
                        <>
                          <Input
                            type="number"
                            id="duration_per_slot"
                            step="1"
                            min="1"
                            max="24"
                            onKeyDown={
                              (e)=>{
                                if(e.key === "."){
                                  e.preventDefault();
                                  return;
                                }
                              }
                            }
                            placeholder="Enter duration in hours"
                            value={value || ""}
                            onChange={(e) => {
                              const newValue = parseFloat(e.target.value);
                              onChange(newValue);

                              // Update end times for selected days based on new duration
                              if (newValue && newValue > 0) {
                                setDays((prev) => {
                                  const newDays = { ...prev };
                                  let hasChanges = false;
                                  const newDayErrors = { ...dayErrors };

                                  Object.keys(newDays).forEach((day) => {
                                    if (newDays[day].is_available) {
                                      const startTime = newDays[day].time_slots.start_time;
                                      const endTime = newDays[day].time_slots.end_time;
                                      const facilityHours = facilityOperatingHours[day];

                                      if (startTime && facilityHours) {
                                        const startMinutes = timeToMinutes(startTime);
                                        const durationMinutes = Math.round(newValue * 60);
                                        const endMinutes = startMinutes + durationMinutes;

                                        const hours = Math.floor(endMinutes / 60);
                                        const minutes = endMinutes % 60;
                                        // Handle overnight (next day)
                                        const displayHours = hours % 24;

                                        const newEndTime = `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

                                        if (newDays[day].time_slots.end_time !== newEndTime) {
                                          newDays[day] = {
                                            ...newDays[day],
                                            time_slots: {
                                              ...newDays[day].time_slots,
                                              end_time: newEndTime
                                            }
                                          };
                                          hasChanges = true;

                                          // Validate the new end time against facility hours
                                          const facilityIsOvernight = isOvernightRange(facilityHours.start_time, facilityHours.end_time);
                                          if (!isTimeWithinFacilityHours(newEndTime, facilityHours.start_time, facilityHours.end_time)) {
                                            newDayErrors[day] = {
                                              ...newDayErrors[day],
                                              end_time: facilityIsOvernight
                                                ? `End time must be between ${facilityHours.start_time} and ${facilityHours.end_time} (overnight range)`
                                                : `End time must be between ${facilityHours.start_time} and ${facilityHours.end_time}`
                                            };
                                          } else {
                                            // Clear error if valid
                                            if (newDayErrors[day]) {
                                              const { end_time, ...rest } = newDayErrors[day];
                                              newDayErrors[day] = rest;
                                            }
                                          }
                                        }
                                      }

                                      // Validate time range against new duration per slot (check updated end time if it was changed)
                                      const currentEndTime = newDays[day].time_slots.end_time;
                                      if (startTime && currentEndTime) {
                                        const timeRangeMinutes = getDurationMinutes(startTime, currentEndTime);
                                        const durationPerSlotMinutes = newValue * 60;

                                        if (timeRangeMinutes > durationPerSlotMinutes) {
                                          const timeRangeHours = timeRangeMinutes / 60;
                                          const errorMessage = `Time range (${timeRangeHours.toFixed(1)} hours) cannot exceed duration per slot (${newValue} hour${newValue !== 1 ? 's' : ''})`;
                                          
                                          newDayErrors[day] = {
                                            ...newDayErrors[day],
                                            end_time: errorMessage,
                                          };
                                        } else {
                                          // Clear duration error if valid
                                          if (newDayErrors[day]?.end_time && newDayErrors[day].end_time.includes('cannot exceed duration per slot')) {
                                            const { end_time, ...rest } = newDayErrors[day];
                                            newDayErrors[day] = Object.keys(rest).length > 0 ? rest : {};
                                          }
                                        }
                                      }
                                    }
                                  });

                                  if (hasChanges) {
                                    setDayErrors(newDayErrors);
                                  }
                                  return hasChanges ? newDays : prev;
                                });
                              }
                            }}
                          />
                          {error && (
                            <span className="text-red-500 text-xs mt-1 block">
                              {error.message}
                            </span>
                          )}
                        </>
                      )}
                    />
                  </div>
                </div>
              </div>

              {facilityId && isFacilityLoading ? (
                <div className="text-blue-600 flex items-center text-xs">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-4 w-4 text-blue-600"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading facility information...
                </div>
              ):<></>}

              {facilityId &&
                !isFacilityLoading &&
                facilityAvailableDays.length === 0 ? (
                  <div className="text-amber-600 flex items-center text-xs">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    Selected facility has no available days yet
                  </div>
                ):<></>}

              {facilityId && !isFacilityLoading && facilityAvailableDays.length > 0 ? (
                <div className="text-blue-600 flex items-center text-xs">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Keep slots within these facility hours
                </div>
              ) : <></>}

              {!facilityId ? (
                <div className="text-amber-600 flex items-center text-xs">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Select a facility to continue
                </div>
              ):<></>}

              {!startDate && !alwaysAvailable && facilityId ? (
                <div className="text-amber-600 flex items-center text-xs">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Pick start & end dates first
                </div>
              ):<></>}

              {startDate && endDate && !alwaysAvailable && facilityId && facilityAvailableDays.length > 0 && visibleDays.length === 0 ? (
                <div className="text-red-600 flex items-center text-xs">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Date range misses all available days. Adjust the filters.
                </div>
              ) : hasInvalidSlotConfiguration ? (
                <div className="text-red-600 flex items-center text-xs">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Duration exceeds the available window. Extend hours or reduce duration.
                </div>
              ) : !hasSelectedDays && ((startDate && !alwaysAvailable) || alwaysAvailable) && Object.entries(days).length > 0 && visibleDays.length > 0 ? (
                <div className="text-red-600 flex items-center text-xs">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Toggle at least one day to proceed
                </div>
              ) : Object.entries(days).length == 0 && (
                <div className="text-red-600 flex items-center text-xs">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  Choose a valid date range to configure days
                </div>
              )}

              {((startDate && !alwaysAvailable) || alwaysAvailable) && visibleDays.length > 0 && (
                <>
                  <div className={`${dayRowGrid} text-xs font-medium text-zinc-500 uppercase tracking-wide`}>
                    <div>Day</div>
                    <div>Start</div>
                    <div>End</div>
                    <div></div>
                  </div>

                  {Object.entries(days).map(([day, value]) => {
                    // Only render this day if it's in the visible days list
                    if (!visibleDays.includes(day)) {
                      return null;
                    }

                    // Get facility operating hours for this day if available
                    const facilityHours = facilityOperatingHours[day];
                    const timeSlots = calculateTimeSlots(day);

                    return (
                      <div key={day} className="mb-5 pb-5 border-b border-gray-100 last:border-b-0">
                        {/* Day Row */}
                        <div data-day={day} className={dayRowGrid}>
                          <div>
                            <button
                              type="button"
                              onClick={() => toggleDay(day)}
                              className={cn(
                                "h-10 px-1 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors flex items-center justify-center w-full truncate",
                                value.is_available
                                  ? "bg-primary text-white"
                                  : "bg-white border border-input text-zinc-600 hover:bg-zinc-100"
                              )}
                            >
                              {day}
                            </button>
                          </div>

                          {/* start time */}
                          <div className="flex flex-col">
                            <Input
                              placeholder="Select Start Time"
                              value={value.time_slots.start_time}
                              onChange={(e) =>
                                updateTime(day, "start_time", e.target.value)
                              }
                              type="time"
                              step="60"
                              disabled={!value.is_available}
                              className={cn(
                                "border-input focus-within:ring-0 h-10",
                                !value.is_available && "opacity-50",
                                dayErrors[day]?.start_time && "border-red-500"
                              )}
                              title={facilityHours ? `Available range: ${facilityHours.start_time} - ${facilityHours.end_time}` : undefined}
                            />
                            {dayErrors[day]?.start_time && (
                              <span className="text-xs text-red-500 mt-1">
                                {dayErrors[day]?.start_time}
                              </span>
                            )}
                            {facilityHours && value.is_available && (
                              <span className="text-xs text-gray-500 mt-1">
                                Facility: {formatTimeRange(facilityHours.start_time, facilityHours.end_time)}
                              </span>
                            )}
                          </div>

                          {/* end time */}
                          <div className="flex flex-col">
                            <Input
                              placeholder="Select End Time"
                              value={value.time_slots.end_time}
                              onChange={(e) =>
                                updateTime(day, "end_time", e.target.value)
                              }
                              type="time"
                              step="60"
                              disabled={!value.is_available}
                              className={cn(
                                "border-input focus-within:ring-0 h-10",
                                !value.is_available && "opacity-50",
                                dayErrors[day]?.end_time && "border-red-500"
                              )}
                              title={facilityHours ? `Available range: ${facilityHours.start_time} - ${facilityHours.end_time}` : undefined}
                            />
                            {dayErrors[day]?.end_time && (
                              <span className="text-xs text-red-500 mt-1">
                                {dayErrors[day]?.end_time}
                              </span>
                            )}
                          </div>

                          {/* Clear Button */}
                          <div className="flex items-center">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => clearDayTimes(day)}
                              disabled={!value.is_available}
                              className={cn(
                                "h-9 w-9 sm:h-10 sm:w-10 p-0 text-destructive shrink-0",
                                !value.is_available && "opacity-50 cursor-not-allowed"
                              )}
                              title="Clear times"
                            >
                              <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Time Slots Preview Row - Below the day */}
                        {value.is_available && (
                          <div className="ml-0 mt-2">
                            {timeSlots.length > 0 ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-1 mb-1">
                                  <Clock className="h-3 w-3 text-gray-500" />
                                  <span className="text-xs font-medium text-gray-600">
                                    {timeSlots.length} slot{timeSlots.length !== 1 ? "s" : ""} generated
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {timeSlots.slice(0, 6).map((slot, index) => (
                                    <div
                                      key={index}
                                      className="text-xs text-gray-700 bg-blue-50 border border-blue-200 px-2 py-1 rounded-md font-mono"
                                    >
                                      {slot}
                                    </div>
                                  ))}
                                  {timeSlots.length > 6 && (
                                    <div className="text-xs text-gray-500 px-2 py-1 rounded-md border border-dashed border-gray-300">
                                      +{timeSlots.length - 6} more
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : value.time_slots.start_time && value.time_slots.end_time && durationPerSlot ? (
                              <div className="text-xs text-red-600 flex items-center">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                <span className="font-medium">Duration too large for this time range</span>
                              </div>
                            ) : value.time_slots.start_time || value.time_slots.end_time ? (
                              <div className="text-xs text-gray-400">
                                Configure both start and end times to see slots
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </CardContent>
          </Card>
        )}
        <div className={cn(
          "flex-1 min-w-0 flex flex-col transition-all duration-300 ease-in-out",
          isFormVisible ? "" : "xl:w-full"
        )}>
          <SlotsandSchedule open={open} />
        </div>
      </div>
    </div>
  );
};

export default EventAvailability;
